import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import { lucideEllipsisVertical, lucideListCollapse, lucidePlus, lucideSave, lucideTrash } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmToggleImports } from '@spartan-ng/helm/toggle';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { filter } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../cartographie/models/cartographie.model';
import { ModuleStore } from '../../référentiel-métier-parcours/parcours-module/store/module.store';
import { ConfirmDialogComponent } from '../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import {
  OrderedListActions,
  OrderedListComponent,
  ReorderEvent,
} from '../../shared/components/ordered-list/ordered-list.component';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { Sequence } from '../models/séquence.model';
import { ÉtatSéquence } from '../models/état-séquence.enum';
import { SequenceStore } from '../store/sequence.store';
import { SéquenceFormComponent } from '../séquence-form/séquence-form.component';
import { DialogConfirmDeleteComponent } from './components/dialog-confirm-delete/dialog-confirm-delete.component';
import { NouvelleSequenceCardComponent } from './components/nouvelle-sequence-card/nouvelle-sequence-card.component';
import { SequenceCardComponent } from './components/sequence-card/sequence-card.component';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-sequence-list',
  imports: [
    HlmButtonImports,
    EmptyStateComponent,
    SequenceCardComponent,
    OrderedListComponent,
    SéquenceFormComponent,
    NouvelleSequenceCardComponent,
    HlmToggleImports,
    HlmIconImports,
    HlmTooltipImports,
    HlmDropdownMenuImports,
  ],
  templateUrl: './sequence-list.component.html',
  providers: [
    provideIcons({
      lucidePlus,
      lucideTrash,
      lucideListCollapse,
      lucideEllipsisVertical,
      lucideSave,
    }),
  ],
})
export class SequenceListComponent implements AfterViewInit, OnDestroy {
  readonly #moduleStore = inject(ModuleStore);
  readonly #sequenceStore = inject(SequenceStore);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #dialogService = inject(HlmDialogService);
  readonly #destroy = inject(DestroyRef);

  readonly idSequence = input<string>();
  readonly idModule = input.required<string>();
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();

  readonly module = computed(() => this.#moduleStore.selectedEntity());
  readonly sequences = computed(() => this.#sequenceStore.entities());

  protected selectedSequence = model(this.#sequenceStore.selectedEntity());

  vueCompacte = model<boolean>(false);

  #ctrlOrCmd = navigator.userAgent.toLowerCase().includes('mac') ? '⌘' : 'Ctrl';

  actions: OrderedListActions[][] = [
    [
      {
        label: 'Ajouter au dessus',
        execute: (sequence: Sequence) => this.addNewSequence(sequence.indice),
        enabled: (sequence: Sequence) => sequence.etat !== ÉtatSéquence.DESACTIVEE,
        shortcut: `${this.#ctrlOrCmd}⇧↑`,
      },
      {
        label: 'Ajouter en dessous',
        execute: (sequence: Sequence) => this.addNewSequence(sequence.indice === undefined ? 0 : sequence.indice + 1),
        enabled: (sequence: Sequence) => sequence.etat !== ÉtatSéquence.DESACTIVEE,
        shortcut: `${this.#ctrlOrCmd}⇧↓`,
      },
    ],
    [
      {
        label: 'Désactiver / Réactiver',
        execute: (sequence: Sequence) => this.toggleSequence(sequence),
        enabled: () => true,
      },
    ],
  ];

  etatSequence = ÉtatSéquence;

  constructor() {
    effect(() => {
      const selectedIdSequence = this.idSequence();
      const sequences = this.#sequenceStore.entities();
      sequences.sort((a, b) => (a.indice! - b.indice! < 0 ? -1 : 1));

      this.selectedSequence.set(null);

      if (!selectedIdSequence || !sequences.map((x) => x.id).includes(selectedIdSequence)) {
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams: { idSequence: sequences[0].id },
          queryParamsHandling: 'merge',
        });
      } else if (selectedIdSequence) {
        this.#sequenceStore.select({ id: selectedIdSequence });
        this.selectedSequence.set(this.#sequenceStore.selectedEntity());
      }
    });

    effect(() => this.#sequenceStore.charger(this.idModule()));
  }

  ngAfterViewInit(): void {
    const selectedIdSequence = this.idSequence();
    if (selectedIdSequence) {
      document.getElementById(selectedIdSequence)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    if (isCtrlOrCmd && event.shiftKey) {
      const sequence = this.selectedSequence();

      if (!sequence) return;

      if (event.key === 'ArrowUp') {
        this.addNewSequence(sequence.indice);
      } else if (event.key === 'ArrowDown') {
        this.addNewSequence(sequence.indice === undefined ? 0 : sequence.indice + 1);
      }
    }
  }

  onOrderChange(rEvent: ReorderEvent<Sequence>) {
    this.#sequenceStore.enregistrerOrdre({
      id: rEvent.item.id,
      request: {
        indice: rEvent.toIndex,
      },
      optimisticSequences: rEvent.newOrder,
      next: () => this.#sequenceStore.charger(this.idModule()),
    });
  }

  onSelectionChange(sequence: Sequence | null) {
    if (sequence !== null) {
      this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { idSequence: sequence.id },
        queryParamsHandling: 'replace',
      });
    }
  }

  deleteSequence(sequence: Sequence) {
    const dialogRef = this.#dialogService.open(DialogConfirmDeleteComponent, {
      context: {
        sequence: sequence,
      },
      contentClass: 'w-[530px]',
    });
    const toastrId = uuidv7();

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroy),
        filter((result: boolean) => !!result),
      )
      .subscribe(() =>
        this.#sequenceStore.supprimer({
          idSéquence: sequence.id,
          next: () => {
            const module = this.module();
            if (module) {
              this.#sequenceStore.charger(module.id);
            }
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'La séquence a bien été supprimée !',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        }),
      );
  }

  toggleSequence(sequence: Sequence) {
    const isEnable = sequence.etat !== ÉtatSéquence.DESACTIVEE;
    const newStatus = isEnable ? ÉtatSéquence.DESACTIVEE : ÉtatSéquence.A_FAIRE;

    const dialogRef = this.#dialogService.open(ConfirmDialogComponent, {
      context: {
        titre: 'Confirmation',
        contenu: `Êtes-vous sûr de vouloir ${isEnable ? 'désactiver' : 'réactiver'} la séquence ${sequence.nom} (${sequence.codeSequence}) ?`,
      },
      contentClass: 'w-[530px]',
    });
    const toastrId = uuidv7();

    dialogRef.closed$
      .pipe(
        takeUntilDestroyed(this.#destroy),
        filter((result: boolean) => !!result),
      )
      .subscribe(() =>
        this.#sequenceStore.modifier({
          id: sequence.id,
          data: { ...sequence, etat: newStatus },
          next: () => {
            const module = this.module();
            if (module) {
              this.#sequenceStore.charger(module.id);
            }
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: `La séquence a bien été ${isEnable ? 'désactivée' : 'activée'} !`,
                id: toastrId,
              },
              id: toastrId,
            });
          },
        }),
      );
  }

  addNewSequence(index?: number) {
    const nouvelleSequence: Sequence = {
      id: 'null',
      codeSequence: '',
      idModule: this.#moduleStore.selectedEntityId()!.toString(),
      indice: index ?? this.#sequenceStore.entities().filter((x) => x.indice !== undefined).length,
      nom: '',
      type: 'Positionnement',
      interrelations: [],
    };
    this.#sequenceStore.ajouterSequence({
      sequence: nouvelleSequence,
    });
    this.onSelectionChange(nouvelleSequence);
  }
}
